import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../hooks/useTheme.jsx';
import { useI18n } from '../../i18n/i18n.jsx';

// SVG Icon Components - Simple & Clean
const HomeIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 10.5 12 3l9 7.5"></path>
    <path d="M5 10v10h14V10"></path>
  </svg>
);

const AboutIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4"></circle>
    <path d="M20 21a8 8 0 0 0-16 0"></path>
  </svg>
);

const TeamIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 14a4 4 0 0 0-8 0"></path>
    <circle cx="12" cy="6" r="2"></circle>
    <path d="M20 21a8 8 0 0 0-16 0"></path>
  </svg>
);

const ServicesIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"></rect>
    <path d="M3 9h18"></path>
    <path d="M9 21V9"></path>
  </svg>
);

const ContactIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="14" rx="2"></rect>
    <path d="M3 7l9 6 9-6"></path>
  </svg>
);

const SettingsIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"></circle>
    <path d="M12 2v2m0 16v2m10-10h-2M2 12H4m14.14 6.36l-1.41-1.41M6.27 6.27L4.86 4.86m12.87 0l-1.41 1.41M6.27 17.73l-1.41 1.41"></path>
  </svg>
);

const SunIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"></circle>
    <path d="M12 1v2m0 18v2m11-11h-2M3 12H1m16.95 6.95l-1.41-1.41M6.46 6.46L5.05 5.05m12.02 0l-1.41 1.41M6.46 17.54l-1.41 1.41"></path>
  </svg>
);

const MoonIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
  </svg>
);

const LanguageIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"></circle>
    <path d="M3 12h18"></path>
    <path d="M12 3a15 15 0 0 0 0 18"></path>
    <path d="M12 3a15 15 0 0 1 0 18"></path>
  </svg>
);

const BottomNav = () => {
  const location = useLocation();
  const { theme, toggleTheme, isDark } = useTheme();
  const { t, language, changeLanguage } = useI18n();
  const [showSettings, setShowSettings] = useState(false);
  const settingsRef = useRef(null);
  const navRef = useRef(null);
  const itemRefs = useRef([]);
  const [activeX, setActiveX] = useState(null);

  // Define navigation items
  const navItems = [
    { path: '/', label: 'Home', icon: HomeIcon },
    { path: '/about', label: 'About', icon: AboutIcon },
    { path: '/team', label: 'Team', icon: TeamIcon },
    { path: '/services', label: 'Services', icon: ServicesIcon },
    { path: '/contact', label: 'Contact', icon: ContactIcon },
    { type: 'settings', label: 'Settings', icon: SettingsIcon },
  ];

  // Get current active index
  const getActiveIndex = () => {
    if (showSettings) {
      return 5; // Settings butonuna balonu kaydır
    }
    const currentIndex = navItems.findIndex(item => 
      item.type === 'settings' ? false : item.path === location.pathname
    );
    return currentIndex >= 0 ? currentIndex : 0;
  };

  const activeIndex = getActiveIndex();

  // Measure active item position
  useEffect(() => {
    const measure = () => {
      const navEl = navRef.current;
      const itemEl = itemRefs.current[activeIndex];
      if (!navEl || !itemEl) return;

      const navRect = navEl.getBoundingClientRect();
      const itemRect = itemEl.getBoundingClientRect();

      // nav içinde item merkezinin X'i (px)
      const x = (itemRect.left + itemRect.width / 2) - navRect.left;
      setActiveX(x);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [activeIndex, showSettings]);

  // Close settings when clicking outside and manage body class
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setShowSettings(false);
      }
    };

    if (showSettings) {
      document.addEventListener('click', handleClickOutside);
      document.body.classList.add('settings-open');
    } else {
      document.body.classList.remove('settings-open');
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.body.classList.remove('settings-open');
    };
  }, [showSettings]);

  // Toggle theme
  const handleToggleTheme = () => {
    toggleTheme();
  };

  // Change language
  const handleChangeLanguage = (lang) => {
    changeLanguage(lang);
    setShowSettings(false);
  };

  return (
    <>
      <nav className={`bottom-nav ${isDark ? 'bottom-nav-dark' : 'bottom-nav-light'}`} aria-label="Alt menü">
        <ul
          ref={navRef}
          className="nav"
          style={{
            '--i': activeIndex,
            '--count': navItems.length,
            ...(activeX != null ? { '--x': `${activeX}px` } : {})
          }}
        >
          {navItems.map((item, index) => {
            const IconComponent = item.icon;
            const isSettings = item.type === 'settings';
            const isSettingsActive = isSettings && showSettings;
            const isActive = !isSettings && !showSettings && location.pathname === item.path;
            
            return (
              <li
                ref={(el) => (itemRefs.current[index] = el)}
                key={item.label}
                className={`item ${isActive ? 'active' : ''} ${isSettingsActive ? 'settings-open' : ''}`}
              >
                {isSettings ? (
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowSettings(!showSettings);
                    }}
                    aria-label={item.label}
                    className="nav-link settings-btn"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                  >
                    <span className="icon">
                      <IconComponent />
                    </span>
                  </button>
                ) : (
                  <Link 
                    to={item.path} 
                    aria-label={item.label}
                    className="nav-link"
                  >
                    <span className="icon">
                      <IconComponent />
                    </span>
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Settings Modal */}
      <div className={`settings-modal ${showSettings ? 'show' : ''}`} ref={settingsRef}>
        <div className="settings-content">
          {/* Theme Toggle */}
          <div className="setting-item">
            <div className="setting-label">
              <span className="setting-icon">
                {isDark ? <MoonIcon /> : <SunIcon />}
              </span>
              <span>{t('theme.toggle')}</span>
            </div>
            <button 
              onClick={handleToggleTheme}
              className={`theme-toggle ${isDark ? 'active' : ''}`}
            >
              <div className="toggle-thumb"></div>
            </button>
          </div>

          {/* Language Selector */}
          <div className="setting-item">
            <div className="setting-label">
              <span className="setting-icon">
                <LanguageIcon />
              </span>
              <span>{t('language.switch')}</span>
            </div>
            <div className="language-buttons">
              <button 
                onClick={() => handleChangeLanguage('en')}
                className={language === 'en' ? 'active' : ''}
              >
                EN
              </button>
              <button 
                onClick={() => handleChangeLanguage('ru')}
                className={language === 'ru' ? 'active' : ''}
              >
                RU
              </button>
              <button 
                onClick={() => handleChangeLanguage('tk')}
                className={language === 'tk' ? 'active' : ''}
              >
                TK
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BottomNav;
